import { useState } from 'react';
import { resolveImagePath } from '@/utils/imageMap';

interface HeaderProps {
  className?: string;
}

export function Header({ className = '' }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header id="masthead" className={`bg-white ${className}`}>
      {/* Skip to content link */}
      <a 
        href="#acc-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-blue-600 text-white px-4 py-2 rounded"
      >
        Skip to content (Press Enter)
      </a>
      
      {/* Top bar with social links and phone */}
      <div className="bg-gray-800 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center">
          {/* Social media links */}
          <ul className="flex space-x-4">
            <li>
              <a
                href="https://www.facebook.com/Islip-Avionics-134923236556774"
                title="Facebook"
                className="text-white hover:text-blue-400 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </li>
            <li>
              <a
                href="https://www.linkedin.com/company/islip-avionics-inc"
                title="LinkedIn"
                className="text-white hover:text-blue-400 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </li>
          </ul>
          
          {/* Phone number */}
          <div>
            <a 
              href="tel:6315883543" 
              className="text-white hover:text-blue-400 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
              </svg>
              <span>(631) 588-3543</span>
            </a>
          </div>
        </div>
      </div>
      
      {/* Main header with logo and navigation */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo and site title */}
          <div className="flex items-center space-x-4">
            <a href="/" className="flex items-center space-x-3">
              <img 
                src={resolveImagePath('/images/logo.png')}
                alt="Islip Avionics"
                className="h-12 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-900 hidden sm:block">
                Islip Avionics
              </h1>
            </a>
          </div>
          
          {/* Mobile menu button */}
          <button
            type="button"
            id="menu-button"
            onClick={toggleMenu}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-expanded={isMenuOpen}
            aria-controls="primary-menu"
          >
            <span className="sr-only">Menu</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          
          {/* Desktop navigation */}
          <nav id="site-navigation" className="hidden md:flex">
            <ul id="primary-menu" className="flex space-x-8">
              <li>
                <a 
                  href="/" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Home
                </a>
              </li>
              <li>
                <a 
                  href="/services/" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Services
                </a>
              </li>
              <li>
                <a 
                  href="/products/" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Products
                </a>
              </li>
              <li>
                <a 
                  href="/work-gallery/" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Our Work Gallery
                </a>
              </li>
              <li>
                <a 
                  href="/about-islip-avionics/" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  About Islip Avionics
                </a>
              </li>
              <li>
                <a 
                  href="/contact/" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>
          </nav>
        </div>
        
        {/* Mobile navigation menu */}
        <div 
          className={`md:hidden bg-white border-t border-gray-200 ${isMenuOpen ? 'block' : 'hidden'}`}
          id="mobile-menu"
        >
          <ul className="py-4 space-y-2">
            <li>
              <a 
                href="/" 
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              >
                Home
              </a>
            </li>
            <li>
              <a 
                href="/services/" 
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              >
                Services
              </a>
            </li>
            <li>
              <a 
                href="/products/" 
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              >
                Products
              </a>
            </li>
            <li>
              <a 
                href="/work-gallery/" 
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              >
                Our Work Gallery
              </a>
            </li>
            <li>
              <a 
                href="/about-islip-avionics/" 
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              >
                About Islip Avionics
              </a>
            </li>
            <li>
              <a 
                href="/contact/" 
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              >
                Contact
              </a>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
}